// Modal dedicado pra atribuir bancos a um grupo (separado da gestão de membros).
// Resolve: "no grupo MED Unicid Karol não consigo ver bancos atribuídos".
//
// Backend já suporta — só faltava uma UI focada. Fluxo:
// 1. Lista bancos atualmente em group.bankIds (com botão remover)
// 2. Picker com busca pra adicionar bancos
// 3. Após adicionar, oferece "Sincronizar grants" (cria bankUserGrants pros members)

import { useEffect, useMemo, useState } from "preact/hooks";
import {
  Archive,
  Check,
  Database,
  Link2,
  Loader2,
  Search,
  Trash2,
  X
} from "lucide-react";
import { api, type BankListItem, type GroupDetail } from "./api";
import { openAlert } from "./ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

export function GroupBanksModal({
  group,
  allBanks,
  onClose
}: {
  group: GroupDetail;
  allBanks: BankListItem[];
  onClose: () => void;
}) {
  const [bankIds, setBankIds] = useState<string[]>(group.bankIds);
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [syncing, setSyncing] = useState(false);

  const bankById = useMemo(() => {
    const m = new Map<string, BankListItem>();
    for (const b of allBanks) m.set(b.id, b);
    return m;
  }, [allBanks]);

  const attached = bankIds
    .map((id) => bankById.get(id))
    .filter((b): b is BankListItem => !!b);

  async function setIds(next: string[]) {
    setBusy("save");
    try {
      await api.patchGroup(group.id, { bankIds: next });
      setBankIds(next);
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(null);
    }
  }
  async function toggle(id: string) {
    // Comportamento de check: se já tá, remove; se não tá, adiciona.
    // Picker fica aberto pra admin marcar vários em sequência sem reclicar
    // em "+ Adicionar banco" a cada vez.
    if (bankIds.includes(id)) {
      await setIds(bankIds.filter((x) => x !== id));
    } else {
      await setIds([...bankIds, id]);
    }
  }
  async function remove(id: string) {
    await setIds(bankIds.filter((x) => x !== id));
  }

  async function sync() {
    setSyncing(true);
    setInfo(null);
    try {
      const r = await api.syncGroupGrants(group.id);
      if (r.message) {
        setInfo(`✓ ${r.message}`);
      } else {
        setInfo(
          `✓ ${r.granted} grant${r.granted !== 1 ? "s" : ""} novo${r.granted !== 1 ? "s" : ""} criado${r.granted !== 1 ? "s" : ""} (${r.skipped} já existiam) — ${r.members} membro${r.members !== 1 ? "s" : ""} × ${r.banks} banco${r.banks !== 1 ? "s" : ""}.`
        );
      }
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSyncing(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[80] grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <Database width="1.2em" height="1.2em" className="text-primary" />
            Bancos do grupo: {group.name}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>

        <div class="flex-1 space-y-3 overflow-y-auto p-4">
          <section class="rounded-md border border-border bg-card/40 p-3">
            <div class="mb-2 flex items-center justify-between">
              <h3 class="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                <Database width="1em" height="1em" />
                Bancos atribuídos ({attached.length})
              </h3>
              {!picking && (
                <button
                  type="button"
                  onClick={() => setPicking(true)}
                  class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold hover:bg-accent"
                >
                  + Adicionar banco
                </button>
              )}
            </div>
            {attached.length === 0 && !picking ? (
              <p class="rounded-md border border-dashed border-border bg-background/30 px-3 py-3 text-center text-[11px] italic text-muted-foreground">
                Nenhum banco atribuído ainda. Use "Adicionar banco" pra começar.
              </p>
            ) : (
              <ul class="space-y-1">
                {attached.map((b) => (
                  <li
                    key={b.id}
                    class={`flex items-center gap-2 rounded-md border border-border bg-background/50 px-2 py-1.5 text-xs ${b.archivedAt ? "opacity-60" : ""}`}
                  >
                    <div class="min-w-0 flex-1">
                      <div class="flex items-center gap-1 truncate font-semibold">
                        {b.archivedAt && (
                          <Archive
                            width="0.85em"
                            height="0.85em"
                            className="text-amber-600 dark:text-amber-400"
                          />
                        )}
                        {b.name}
                      </div>
                      <div class="font-mono text-[10px] text-muted-foreground">
                        {b.questionCount} questões · v{b.version}
                        {b.companyName && ` · ${b.companyName}`}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => remove(b.id)}
                      disabled={busy === "save"}
                      title="Remover"
                      aria-label="Remover"
                      class="rounded p-1 text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
                    >
                      <Trash2 width="0.9em" height="0.9em" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>

          {picking && (
            <BankPicker
              allBanks={allBanks}
              selectedIds={new Set(bankIds)}
              onToggle={toggle}
              onClose={() => setPicking(false)}
              busy={busy === "save"}
            />
          )}

          <section class="rounded-md border border-violet-500/30 bg-violet-500/5 p-3">
            <div class="flex items-start gap-3">
              <Link2
                width="1.2em"
                height="1.2em"
                className="mt-0.5 shrink-0 text-violet-600 dark:text-violet-300"
              />
              <div class="min-w-0 flex-1">
                <div class="text-xs font-bold text-violet-700 dark:text-violet-300">
                  Sincronizar acesso pros membros do grupo
                </div>
                <p class="mt-0.5 text-[11px] text-muted-foreground">
                  Cria grants diretos pra cada par (membro × banco). Idempotente — não toca
                  grants existentes.
                </p>
                {attached.length > 0 && group.memberCount > 0 && (
                  <p class="mt-0.5 text-[10px] text-violet-700 dark:text-violet-300">
                    {group.memberCount} membro{group.memberCount !== 1 ? "s" : ""} × {attached.length} banco{attached.length !== 1 ? "s" : ""} = até {group.memberCount * attached.length} grant{group.memberCount * attached.length !== 1 ? "s" : ""}.
                  </p>
                )}
              </div>
              <button
                type="button"
                onClick={sync}
                disabled={syncing || attached.length === 0 || group.memberCount === 0}
                class="inline-flex shrink-0 items-center gap-1 rounded-md border border-violet-500/40 bg-violet-500/15 px-2 py-1 text-[11px] font-bold text-violet-700 hover:bg-violet-500/25 disabled:opacity-50 dark:text-violet-300"
              >
                {syncing ? (
                  <Loader2 width="1em" height="1em" className="animate-spin" />
                ) : (
                  <Link2 width="1em" height="1em" />
                )}
                Sincronizar
              </button>
            </div>
          </section>

          {info && (
            <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-xs text-emerald-700 dark:text-emerald-300">
              {info}
            </div>
          )}
        </div>

        <div class="border-t border-border bg-card px-4 py-3 text-right">
          <button
            type="button"
            onClick={onClose}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
}

function BankPicker({
  allBanks,
  selectedIds,
  onToggle,
  onClose,
  busy
}: {
  allBanks: BankListItem[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onClose: () => void;
  busy: boolean;
}) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const candidates = allBanks.filter((b) => !b.archivedAt);
    if (!term) return candidates.slice(0, 50);
    return candidates
      .filter((b) =>
        `${b.name} ${b.description ?? ""} ${b.companyName ?? ""} ${b.tags.join(" ")}`
          .toLowerCase()
          .includes(term)
      )
      .slice(0, 50);
  }, [allBanks, q]);

  return (
    <div class="space-y-2 rounded-md border border-primary/40 bg-primary/5 p-2">
      <div class="flex items-center gap-2">
        <div class="relative flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={q}
            onInput={(e) => setQ((e.target as HTMLInputElement).value)}
            placeholder="Buscar banco por nome, descrição, company, tag…"
            autoFocus
            aria-label="Buscar banco"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          onClick={onClose}
          class="shrink-0 rounded-md border border-border bg-background px-3 py-1.5 text-[11px] font-semibold hover:bg-accent"
        >
          Fechar lista
        </button>
      </div>
      <p class="text-[10px] text-muted-foreground px-1">
        Clique pra marcar/desmarcar. A lista fica aberta até você fechar.
      </p>
      <div class="max-h-[320px] overflow-y-auto rounded-md border border-border bg-background">
        {filtered.length === 0 ? (
          <p class="p-3 text-center text-xs italic text-muted-foreground">
            Nenhum banco encontrado.
          </p>
        ) : (
          <ul class="divide-y divide-border">
            {filtered.map((b) => {
              const checked = selectedIds.has(b.id);
              return (
                <li key={b.id}>
                  <button
                    type="button"
                    onClick={() => onToggle(b.id)}
                    disabled={busy}
                    class={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors disabled:opacity-50 ${
                      checked
                        ? "bg-primary/10 hover:bg-primary/15"
                        : "hover:bg-accent/30"
                    }`}
                  >
                    <span
                      class={`inline-flex size-4 shrink-0 items-center justify-center rounded border ${
                        checked
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-background"
                      }`}
                      aria-hidden="true"
                    >
                      {checked && <Check width="0.75em" height="0.75em" />}
                    </span>
                    <span class="min-w-0 flex-1">
                      <span class="block truncate font-semibold">{b.name}</span>
                      <span class="block truncate text-[10px] text-muted-foreground">
                        {b.companyName ?? "Sem company"} · {b.questionCount} questões
                      </span>
                    </span>
                    {busy && (
                      <Loader2
                        width="0.9em"
                        height="0.9em"
                        className="animate-spin text-muted-foreground"
                      />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
